import React from "react";
import {
  Box, Checkbox, Divider, FormControlLabel, IconButton, MenuItem,
  Popover, Stack, TextField, Tooltip, Typography, Button, Collapse,
} from "@mui/material";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import SwapVertIcon from "@mui/icons-material/SwapVert";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { LegacyTokens, TypeScale } from "../../../../theme/b13Tokens";
import { B13 } from "../../../../theme/b13Tokens";
import { OutcomeMode, OUTCOME_MODE_LABELS } from "./outcomeModeConstants";

// Template-scoped settings popover anchored to the gear icon next to
// Template Name. Popover opens with its BOTTOM aligned to the gear's
// TOP so the panel sits HIGHER — near the Template Name row rather
// than dangling toward the bottom of the modal. MUI's marginThreshold
// keeps it inside the viewport; if there is not enough room above,
// MUI clamps it into view rather than flipping direction, which is
// acceptable at all supported desktop sizes because the Template
// Name row sits well below viewport top inside the Dialog.
//
// Content is ordered so that Outcome Measurement + Advanced settings —
// the clinically important controls — appear at the TOP of the panel,
// not below the description / default / reverse-order items.

export type TemplateSettingsMenuProps = {
  description: string;
  onDescriptionChange: (v: string) => void;
  isDefault: boolean;
  onDefaultChange: (v: boolean) => void;
  direction: string;
  onDirectionChange: (v: string) => void;
  onReverse: () => void;
  outcomeMode: OutcomeMode;
  onOutcomeModeChange: (m: OutcomeMode) => void;
  disabled?: boolean;
};

export default function TemplateSettingsMenu(props: TemplateSettingsMenuProps) {
  const [anchor, setAnchor] = React.useState<HTMLElement | null>(null);
  const [advOpen, setAdvOpen] = React.useState(false);
  const open = Boolean(anchor);

  return (
    <>
      <Tooltip title="Template settings" arrow>
        <span>
          <IconButton
            onClick={(e) => setAnchor(e.currentTarget)}
            disabled={props.disabled}
            aria-label="Template settings"
            sx={{ color: LegacyTokens.ink3 }}
          >
            <SettingsOutlinedIcon />
          </IconButton>
        </span>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchor}
        onClose={() => { setAnchor(null); setAdvOpen(false); }}
        // Open UPWARD from the gear: anchor at the gear's top-right,
        // align popover's bottom-right to that point. Panel therefore
        // sits ABOVE the gear whenever there is room; MUI clamps to
        // the viewport (marginThreshold=16) if there is not.
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        transformOrigin={{ vertical: "bottom", horizontal: "right" }}
        sx={{ zIndex: (t) => t.zIndex.modal + 2 }}
        marginThreshold={16}
        data-testid="template-settings-popover"
        PaperProps={{
          "data-testid": "template-settings-paper",
          sx: {
            p: 0,
            width: 380,
            maxWidth: "calc(100vw - 32px)",
            maxHeight: "calc(100vh - 80px)",
            overflowY: "auto",
            overflowX: "hidden",
            borderRadius: `${LegacyTokens.radius.control}px`,
            boxShadow: "0 6px 20px rgba(15, 61, 46, 0.14)",
            // 8px gap between gear and popover bottom edge.
            mb: 1,
          },
        }}
      >
        <Box sx={{ p: 2.5 }} data-testid="template-settings-body">
          <Typography sx={{ ...TypeScale.sectionHead, mb: 1.5 }}>
            Template settings
          </Typography>

          <Stack spacing={2}>
            {/* 1) Ordering direction */}
            <TextField
              select fullWidth size="small" label="Ordering direction"
              value={props.direction}
              onChange={(e) => props.onDirectionChange(e.target.value)}
              InputLabelProps={{ shrink: true }}
            >
              <MenuItem value="most_to_least">Most-to-Least</MenuItem>
              <MenuItem value="least_to_most">Least-to-Most</MenuItem>
              <MenuItem value="custom">Custom</MenuItem>
            </TextField>

            {/* 2) Outcome Measurement — promoted OUT of the Advanced
                collapse per ticket §6. Two options rendered as an
                inline segmented control so Successful/Unsuccessful and
                Weighted are always visible without an extra click. */}
            <Box data-testid="outcome-measurement-section">
              <Typography sx={{ ...TypeScale.fieldLabel, mb: 0.75, color: LegacyTokens.ink3 }}>
                Outcome Measurement
              </Typography>
              <Box
                role="radiogroup"
                aria-label="Outcome Measurement"
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 1,
                }}
              >
                {(Object.keys(OUTCOME_MODE_LABELS) as OutcomeMode[]).map((mode) => {
                  const selected = props.outcomeMode === mode;
                  return (
                    <Box
                      key={mode}
                      role="radio"
                      aria-checked={selected}
                      tabIndex={0}
                      data-outcome-mode={mode}
                      onClick={() => props.onOutcomeModeChange(mode)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          props.onOutcomeModeChange(mode);
                        }
                      }}
                      sx={{
                        px: 1.5, py: 1,
                        borderRadius: `${LegacyTokens.radius.control}px`,
                        border: `1px solid ${selected ? B13.primary : LegacyTokens.line}`,
                        backgroundColor: selected ? "rgba(26,115,232,0.08)" : LegacyTokens.surface,
                        color: selected ? B13.primary : LegacyTokens.ink,
                        fontSize: 13,
                        fontWeight: selected ? 600 : 500,
                        cursor: "pointer",
                        textAlign: "center",
                        userSelect: "none",
                        transition: "background-color 120ms, border-color 120ms",
                        "&:hover": { borderColor: B13.primary },
                        "&:focus-visible": { outline: `2px solid ${B13.primary}`, outlineOffset: 2 },
                      }}
                    >
                      {OUTCOME_MODE_LABELS[mode]}
                    </Box>
                  );
                })}
              </Box>
            </Box>

            {/* 3) Advanced settings — kept as a collapsible for future
                additions. Currently empty of unique items (Outcome
                Measurement moved above); collapse header is present
                so the affordance is preserved without hiding anything
                important behind it. */}
            <Box data-testid="advanced-settings-section">
              <Box
                role="button"
                tabIndex={0}
                onClick={() => setAdvOpen((v) => !v)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setAdvOpen((v) => !v); }
                }}
                sx={{
                  display: "flex", alignItems: "center", gap: 0.5,
                  fontSize: 12, fontWeight: 700, letterSpacing: 0.2,
                  color: LegacyTokens.ink3,
                  cursor: "pointer",
                  userSelect: "none",
                  "&:hover": { color: B13.primary },
                }}
              >
                {advOpen ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
                Advanced settings
              </Box>
              <Collapse in={advOpen} unmountOnExit>
                <Box sx={{ pt: 1, pl: 3 }}>
                  <Typography sx={{ fontSize: 12, color: LegacyTokens.ink3 }}>
                    No additional advanced settings.
                  </Typography>
                </Box>
              </Collapse>
            </Box>

            <Divider sx={{ borderColor: LegacyTokens.line }} />

            {/* 4) Description */}
            <TextField
              fullWidth size="small" label="Description"
              value={props.description}
              onChange={(e) => props.onDescriptionChange(e.target.value)}
              multiline minRows={2}
              InputLabelProps={{ shrink: true }}
            />

            {/* 5) Default template */}
            <FormControlLabel
              sx={{ m: 0 }}
              control={
                <Checkbox
                  checked={props.isDefault}
                  onChange={(e) => props.onDefaultChange(e.target.checked)}
                  size="small"
                />
              }
              label={
                <Typography sx={{ fontSize: 13, color: LegacyTokens.ink2 }}>
                  Use as default template when adding new programs
                </Typography>
              }
            />

            {/* 6) Reverse level order */}
            <Box>
              <Button
                size="small"
                startIcon={<SwapVertIcon />}
                onClick={() => props.onReverse()}
                sx={{ color: B13.primary, textTransform: "none", fontWeight: 600 }}
              >
                Reverse level order
              </Button>
            </Box>
          </Stack>
        </Box>
      </Popover>
    </>
  );
}
